import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton } from '@mui/material';
import { type GridRenderCellParams, type GridRowId, type GridRowModel, GridRowModes, useGridApiContext } from '@mui/x-data-grid-pro';
import { type GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';
import { type FC, useCallback, useContext, forwardRef } from 'react';

import { GridRowContext } from '@/app/shared/components/Grid/CPGrid';
import { gridRowActionsIconProps, navigateToLastGridRow, newGridRecordId } from '@/app/shared/utils/grid';
import { GridColActionsWrapper } from './grid.styles';

interface IGridColActionsProps {
  name?: string;
  canNotCopy?: boolean;
  canNotEdit?: boolean;
  params: GridRenderCellParams;
  isRowValid: boolean;
  pageSize: number;
  setEditableRowId: (rowId: GridRowId | null) => void;
  editableRowId: GridRowId | null;
  ref?: React.MutableRefObject<GridApiPro>;
  rowActionsSlot?: FC<GridRowModel>;
  onSaveClick?: () => void;
  onCopyClick?: (row: GridRowModel) => void;
  onEditClick?: (row: GridRowModel) => void;
  onCancelClick?: (row:GridRowModel) => void;
}

export const GridColActions = forwardRef(
  (
    {
      canNotCopy,
      canNotEdit,
      params,
      onSaveClick,
      onCopyClick,
      onEditClick,
      onCancelClick,
      pageSize,
      rowActionsSlot: RowActionsSlot,
      setEditableRowId,
      editableRowId,
    }: IGridColActionsProps,
    _ref,
  ) => {
    const api = useGridApiContext();
    const rowContext = useContext(GridRowContext);
    const { id, row } = params;

    const isEditMode = api.current.getRowMode(id) === GridRowModes.Edit;

    const handleEditClick = useCallback(
      async(event: React.MouseEvent) => {
        event.stopPropagation();
        if (editableRowId !== null) {
          const editableRowMode = api.current.getRowMode(editableRowId) === GridRowModes.Edit;
          editableRowId === newGridRecordId && api.current.updateRows([{id: editableRowId, _action: 'delete'}]);
          editableRowMode && (await api.current.stopRowEditMode({ id: editableRowId, ignoreModifications: true }));
          rowContext.resetErrors();
        }

        if (onEditClick) {
          onEditClick(row);
        } else {
          await api.current.startRowEditMode({ id });
        }

        setEditableRowId(id);
      },
      [row, id, editableRowId, setEditableRowId],
    );

    const handleSaveClick = useCallback((event: React.SyntheticEvent) => {
      event.stopPropagation();
      onSaveClick?.();
    }, [onSaveClick]);

    const handleCancelClick = useCallback((event: React.SyntheticEvent) => {
      event.stopPropagation();

      rowContext.resetErrors();

      setEditableRowId(null);

      if (id === newGridRecordId || row.isNew) {
        api.current.updateRows([{ id, _action: 'delete' }]);
      }

      api.current.stopRowEditMode({ id, ignoreModifications: true });

      onCancelClick?.(row);
    }, [rowContext, api.current, id, row, onCancelClick]);

    const handleDuplicateClick = useCallback(async(event: React.MouseEvent) => {
      event.stopPropagation();

      if (editableRowId !== null) {
        const editableRowMode = api.current.getRowMode(editableRowId) === GridRowModes.Edit;
        editableRowMode && (await api.current.stopRowEditMode({ id: editableRowId, ignoreModifications: true }));
        rowContext.resetErrors();
      }

      if (onCopyClick) {
        onCopyClick?.(row);
      } else {
        const id = newGridRecordId;
        const updatedRow = [{ ...row, id, _id: row.id, isNew: true }];
        await api.current.updateRows(updatedRow);
        await api.current.startRowEditMode({ id });
      }

      navigateToLastGridRow(api.current, pageSize);

      setEditableRowId(id);
    }, [api.current, pageSize, row, isEditMode, editableRowId]);

    return (
      <GridColActionsWrapper>
        {isEditMode ? (
          <>
            <IconButton
              onClick={handleSaveClick}
              onKeyUp={(event) => event.key === 'Enter' && handleSaveClick(event)}
            >
              <CheckIcon sx={gridRowActionsIconProps} />
            </IconButton>
            <IconButton
              onClick={handleCancelClick}
              onKeyUp={(event) => event.key === 'Enter' && handleCancelClick(event)}
            >
              <CloseIcon sx={gridRowActionsIconProps} />
            </IconButton>
          </>
        ) : (
          <>
          {!canNotEdit && (
              <IconButton data-action="edit-row" tabIndex={2} onClick={handleEditClick}>
                <EditIcon sx={gridRowActionsIconProps} />
              </IconButton>
            )}
            
            {!canNotCopy && (
              <IconButton disabled={isEditMode} tabIndex={2} onClick={handleDuplicateClick}>
                <ContentCopyIcon sx={gridRowActionsIconProps} />
              </IconButton>
            )}

            {RowActionsSlot && <RowActionsSlot {...params} />}
          </>
        )}
      </GridColActionsWrapper>
    );
  },
);


